// src/utils/image.js
// 画像アップロード前のリサイズ・圧縮ユーティリティ（ブラウザの canvas を利用）

const DEFAULT_MAX_EDGE = 1600; // 長辺の最大px
const DEFAULT_QUALITY = 0.8; // JPEG品質

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * 画像を長辺 maxEdge 以下 / JPEG品質 quality にリサイズ・圧縮する。
 * - 画像以外のファイルはそのまま返す
 * - 長辺が maxEdge 以下の場合は拡大しない
 * - 変換に失敗した場合・元より大きくなった場合は元ファイルを返す
 * @param {File} file
 * @param {{maxEdge?: number, quality?: number}} [opts]
 * @returns {Promise<File>}
 */
export async function resizeImage(file, opts = {}) {
  const maxEdge = opts.maxEdge ?? DEFAULT_MAX_EDGE;
  const quality = opts.quality ?? DEFAULT_QUALITY;

  if (!file || !file.type || !file.type.startsWith("image/")) return file;

  try {
    const dataUrl = await readFileAsDataURL(file);
    const img = await loadImage(dataUrl);

    const longEdge = Math.max(img.width, img.height);
    const scale = Math.min(1, maxEdge / longEdge); // 1600px以下なら拡大しない
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality)
    );
    if (!blob) return file;
    // 圧縮しても小さくならなければ元ファイルを使う
    if (blob.size >= file.size) return file;

    const baseName = (file.name || "upload").replace(/\.[^.]+$/, "");
    return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
  } catch (err) {
    console.warn("画像リサイズに失敗（元ファイルを使用）:", err);
    return file;
  }
}
